/**
 * Admin / Workers observability.
 *
 * Backed by GET /v1/admin/workers — the API scrapes each worker's
 * /status surface in parallel (workers/internal/status) and returns
 * the aggregate. Platform-only.
 *
 * Polled every 10s when the admin tab is visible so a stuck or
 * misconfigured worker surfaces quickly. Pause when the tab is
 * hidden to avoid burning the (cheap, but pointless) scrape budget.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface WorkerRegistryEntry {
  id: number;
  name: string;
}

export interface WorkerStatus {
  /** Canonical short id, e.g. "smsworker", "dlr-receiver". */
  name: string;
  /** /status URL the API scraped. Surfaced for debugging only. */
  url: string;
  reachable: boolean;
  /** Non-empty when scrape failed (timeout, refused, HTTP 5xx, decode). */
  error?: string;
  /** RFC3339 of the worker process boot time. */
  started_at?: string;
  uptime_seconds?: number;
  go_version?: string;
  goroutines?: number;
  /** RabbitMQ queues the worker is consuming from. Empty for pure
   *  HTTP-listener workers (dlr-receiver). */
  queues?: string[];
  registry?: WorkerRegistryEntry[];
  registry_count: number;
  processed: number;
  errors: number;
  /** RFC3339 of the most recent processed/error tick (whichever wins).
   *  Empty when the worker hasn't processed anything since boot. */
  last_activity_at?: string;
  latency_ms: number;
}

export interface AdminWorkersResponse {
  items: WorkerStatus[];
}

const WORKERS_KEY = ['admin', 'workers'] as const;

export function useAdminWorkers(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: WORKERS_KEY,
    queryFn: () => api.get<AdminWorkersResponse>('/v1/admin/workers'),
    enabled: opts?.enabled ?? true,
    // 10s poll keeps the page lively without hammering the workers.
    // The API scrapes with a 3s per-worker cap so the worst-case
    // response latency stays bounded even if every worker hangs.
    refetchInterval: opts?.enabled === false ? false : 10_000,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });
}
